import { Box, MainText } from "app/components/common";
import React, { useEffect, useState } from "react";
import { ScrollView, StatusBar, StyleSheet } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { getUserId } from "@microdoing/shared/services/user/selectors";
import {
  selectRecommendedStepRequest,
  acceptPerfectStepRequest,
  selectStep,
} from "@microdoing/shared/services/userCampaign/actions";
import {
  getHasRequestedForNewActions,
  getRecommendedStepsInOrder,
  getStepSelected,
  getTrackId,
  getUserCampaignId,
  getCampaignRequesting,
} from "@microdoing/shared/services/userCampaign/selectors";
import CarouselCards from "./CarouselCards";
import i18n from "@microdoing/shared/i18n";
import moment from "moment";
import theme from "app/Theme/Theme";
import {
  isIos,
  isWeb,
  isWebDesktop,
} from "@microdoing/shared/utils/deviceDetectors";
import FullScreenCard from "./FullScreenCard";
import { changeSeeActionAgain } from "@microdoing/shared/services/action/actions";
import CommentList from "app/scenes/FeedbackScreen/ReflexiveAnalysis/Action/CommentList";
import { breadCrumbsSelectActionScreen } from "app/navigation/breadcrumb/BreadcrumbPaths";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import { OpenableCard } from "app/components/common/Cards/MainCard/OpenableCard.web";
import LinearGradient from "react-native-linear-gradient";
import { useNavigation } from "@react-navigation/native";
import FullScreenCardSkeleton from "./FullScreenCardSkeleton";
import HeaderFullScreen from "app/components/common/HeaderFullScreen";
import RefreshIcon from "app/components/Icons/RefrreshIcon";
import normalize from "app/utils/normalize";
import ArrowLeft from "app/components/Icons/Arrow/ArrowLeft";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const styles = StyleSheet.create({
  main: {
    height: "100%",
    flex: 1,
    display: "flex",
    width: "100%",
    alignItems: isWebDesktop ? "flex-start" : "center",
    flexDirection: "column",
    backgroundColor: theme.colors["neutral.75"],
  },
  backButton: {
    zIndex: 1000,
    marginLeft: theme.spacing.s,
    alignSelf: "flex-start",
  },
  scrollView: {
    flex: 1,
    scrollbarWidth: "thin",
  },
});

const SelectActionScreen = ({}) => {
  const [loading, setLoading] = useState(true);

  const userId = useSelector(getUserId);
  const trackId = useSelector(getTrackId);
  const stepSelected = useSelector(getStepSelected);
  const recommendedSteps = useSelector(getRecommendedStepsInOrder);
  const requesting = useSelector(getCampaignRequesting);
  const userCampaignId = useSelector(getUserCampaignId);

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const insets = useSafeAreaInsets();

  useEffect(() => {
    setTimeout(() => setLoading(false), 750);
    dispatch(selectRecommendedStepRequest({ userId, userCampaignId, trackId }));
  }, [userId, userCampaignId, trackId, selectRecommendedStepRequest]);

  const hasRequestedForNewActionsDate = useSelector(
    getHasRequestedForNewActions,
  );

  const onPressAcceptStep = item => async () => {
    const stepId = item._id;
    await dispatch(
      acceptPerfectStepRequest({
        userCampaignId,
        stepId,
      }),
    );
  };

  const askNewActions = () => {
    dispatch(
      selectRecommendedStepRequest({
        userCampaignId,
        userId,
        trackId,
        hasRequestedForNewActions: moment().toDate(),
      }),
    );
  };

  const hasNewActions = () => {
    // Only one possibility every day;
    if (!hasRequestedForNewActionsDate) return true;
    const date = moment(hasRequestedForNewActionsDate).isSame(moment(), "day");

    return date;
  };

  const handleSeeAction = item => () => {
    if (
      !Boolean(stepSelected?._id)
        ? item?._id
        : stepSelected?._id !== item?._id
        ? item?._id
        : null
    ) {
      dispatch(
        selectStep({
          stepSelected: item,
        }),
      );
    } else {
      dispatch(selectStep({ stepSelected: {} }));
    }
    dispatch(changeSeeActionAgain(false));
    // navigation.navigate("Action");
  };

  const onPressSocialSheetDesktop = item => () => {
    if (item?.numberOfComments > 0) {
      if (
        !Boolean(stepSelected?._id)
          ? item?._id
          : stepSelected?._id !== item?._id
          ? item?._id
          : null
      ) {
        dispatch(selectStep({ stepSelected: item }));
      } else {
        dispatch(selectStep({ stepSelected: {} }));
      }
    }
  };

  return (
    <WebDesktopView breadcrumbsPath={breadCrumbsSelectActionScreen}>
      <Box
        height="100%"
        flex={1}
        width="100%"
        alignItems={isWebDesktop ? "flex-start" : "center"}
        flexDirection="column"
        backgroundColor="neutral.75"
        style={{ paddingTop: insets.top ? insets.top : theme.spacing.xs }}
      >
        <StatusBar animated backgroundColor={theme.colors["neutral.75"]} />
        {!isWebDesktop && (
          <>
            <HeaderFullScreen
              leftIcon={<ArrowLeft color={theme.colors["neutral.900"]} />}
              title={i18n.t("action-of-the-day")}
              marginTop={isWeb ? "xs" : "null"}
              marginBottom="xs"
              rightIcon={
                <>
                  {hasNewActions() && (
                    <Box flexDirection="row">
                      <MainText
                        variant="l3-medium"
                        marginRight="xxs"
                        color="brand.500"
                      >
                        {i18n.t("get-new-actions")}
                      </MainText>
                      <RefreshIcon
                        color={theme.colors["brand.500"]}
                        height={normalize(16)}
                        width={normalize(16)}
                      />
                    </Box>
                  )}
                </>
              }
              callbackOnPressRight={askNewActions}
            />
          </>
        )}
        {loading || requesting ? (
          <FullScreenCardSkeleton isLoading={loading || requesting} />
        ) : !isWebDesktop ? (
          <CarouselCards
            navigation={navigation}
            recommendedSteps={recommendedSteps}
            onPressAcceptStep={onPressAcceptStep}
          />
        ) : (
          <Box flex={1} width="100%">
            <LinearGradient
              pointerEvents="none"
              start={{ x: 0, y: 0 }}
              end={{ x: 0, y: 1 }}
              style={{
                position: "absolute",
                height: "100%",
                width: "1%",
                bottom: 0,
                zIndex: 1,
              }}
              colors={[theme.colors["neutral.75"], "rgba(255,255,255,0)"]}
            />
            <ScrollView
              style={{
                flex: 1,
                paddingVertical: theme.spacing.m,
                height: "100%",
                paddingLeft: theme.spacing.s,
                paddingBottom: isWebDesktop
                  ? theme.spacing.sm
                  : theme.spacing.m,
              }}
              contentContainerStyle={styles.scrollView}
              horizontal
            >
              <Box flex={1} flexDirection="row" alignItems="center">
                {recommendedSteps.map((item, i) => {
                  const isOpen = stepSelected._id === item._id;
                  return (
                    <OpenableCard
                      isOpen={isOpen}
                      leftSide={
                        <FullScreenCard
                          // onPressSocialSheet={onPressSocialSheetDesktop(item)}
                          buttonText={i18n.t("accept-action")}
                          item={item}
                          onPress={handleSeeAction(item)}
                          onPressButton={onPressAcceptStep(item)}
                          index={`${i}`}
                          isSocialSheetOpen={isOpen}
                        />
                      }
                      openableSide={
                        <Box
                          height="100%"
                          backgroundColor="neutral.white"
                          shadowColor="shadow"
                          shadowRadius={10}
                          shadowOpacity={1}
                          shadowOffset={{ height: 4, width: 0 }}
                          borderTopRightRadius={16}
                          borderBottomRightRadius={16}
                          paddingVertical="m"
                          paddingLeft={isOpen ? "xxl" : 0}
                          style={{ marginLeft: -25 }}
                        >
                          <CommentList />
                        </Box>
                      }
                    />
                  );
                })}
              </Box>
            </ScrollView>
          </Box>
        )}
      </Box>
    </WebDesktopView>
  );
};

export default SelectActionScreen;
